import React, { useEffect, useState, useRef } from "react";
import { Scroll } from "@react-three/drei";

const HTMLSections = ({ currentScene }) => {
  const [visibleElements, setVisibleElements] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    // if (currentScene !== 'about') {
    //   // Reset when leaving about section
    //   setVisibleElements(new Set());
    //   return;
    // }

    // Create intersection observer for scroll-triggered animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id) {
            if (entry.isIntersecting) {
              // Element entered viewport - animate in
              setVisibleElements((prev) => new Set([...prev, id]));
            } else {
              // Element left viewport - animate out (reverse)
              setVisibleElements((prev) => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
              });
            }
          }
        });
      },
      {
        threshold: 0.2, // Trigger when 20% of element is visible
        rootMargin: "0px 0px -100px 0px", // Start animation slightly before element enters
      },
    );

    // Observe all elements with data-animate-id
    const elements = document.querySelectorAll("[data-animate-id]");
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [currentScene]);

  return (
    <Scroll html>
      {/* About Section */}
      <div
        style={{
          position: "absolute",
          top: currentScene === "about" ? "460vh" : "-10000px", // Position AT the scroll target
          left: 0,
          width: "100vw",
          minHeight: "200vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          pointerEvents: currentScene === "about" ? "auto" : "none",
        }}
      >
        <div
          className="w-full flex flex-col justify-around items-center px-4 py-8 md:px-8 md:py-16 lg:px-8 lg:py-40"
          style={{
            minHeight: "200vh",
          }}
        >
          {/* Title */}
          <h1
            data-animate-id="about-title"
            className={`text-3xl md:text-5xl lg:text-7xl font-bold text-white text-center mb-8 md:mb-16 lg:mb-32 transition-all duration-1000 ease-out ${
              visibleElements.has("about-title")
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-10"
            }`}
            style={{ fontFamily: "Azonix, sans-serif" }}
          >
            Who is Cheryl Toh?
          </h1>

          {/* Section 1: My Coding Journey */}
          <div
            style={{ width: "80%", maxWidth: "72rem", marginBottom: "6rem" }}
            className="mb-12 md:mb-24 lg:mb-48"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 lg:gap-20 items-center">
              {/* Text */}
              <div
                data-animate-id="journey-text"
                className={`lg:col-span-2 transition-all duration-1000 ease-out ${
                  visibleElements.has("journey-text")
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-10"
                }`}
              >
                <h2
                  className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6"
                  style={{ fontFamily: "Azonix, sans-serif" }}
                >
                  My Coding Journey
                </h2>
                <p
                  className="text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed"
                  style={{
                    fontFamily: "Modeka, sans-serif",
                    fontSize: "clamp(0.9rem, 2vw, 1.2rem)",
                  }}
                >
                  I'm a full-stack developer driven by a passion for constant
                  creation and improvement. Whether creating seamless user
                  experiences or building robust back-end systems, I embrace
                  every challenge as an opportunity to learn and grow. My
                  journey is defined by relentless curiosity, technical mastery,
                  and commitment to pushing web development boundaries.
                </p>
              </div>

              {/* Image */}
              <div
                data-animate-id="journey-image"
                className={`lg:col-span-1 transition-all duration-1000 ease-out delay-200 ${
                  visibleElements.has("journey-image")
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-10"
                }`}
              >
                <div
                  className="w-full overflow-hidden shadow-2xl"
                  style={{ maxWidth: "100%", height: "auto" }}
                >
                  <img
                    src="/about1.png"
                    alt="Cheryl Toh - Developer"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Cheryl's World */}
          <div
            style={{ width: "80%", maxWidth: "72rem" }}
            className="mb-12 md:mb-16 lg:mb-32"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 lg:gap-12 items-center">
              {/* Image - on the left for desktop, order-first on mobile */}
              <div
                data-animate-id="world-image"
                className={`lg:col-span-1 transition-all duration-1000 ease-out order-2 md:order-1 ${
                  visibleElements.has("world-image")
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-10"
                }`}
              >
                <div
                  className="overflow-hidden shadow-2xl w-full"
                  style={{ maxWidth: "100%", height: "auto" }}
                >
                  <img
                    src="/about2.png"
                    alt="Cheryl's World"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              {/* Text - on the right for desktop, order-second on mobile */}
              <div
                data-animate-id="world-text"
                className={`lg:col-span-2 transition-all duration-1000 ease-out delay-200 order-1 md:order-2 ${
                  visibleElements.has("world-text")
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-10"
                }`}
              >
                <h2
                  className="text-2xl md:text-3xl font-semibold text-white mb-3 md:mb-4"
                  style={{ fontFamily: "Azonix, sans-serif" }}
                >
                  Cheryl's World
                </h2>
                <p
                  className="text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed mb-4 md:mb-6"
                  style={{
                    fontFamily: "Modeka, sans-serif",
                    fontSize: "clamp(0.9rem, 2vw, 1.2rem)",
                  }}
                >
                  Beyond coding, I have a deep passion for music and once had
                  the privilege of serving as the president of Sunway Music
                  Society. This experience fueled my love for creativity and
                  collaboration. My ultimate dream is to contribute to a better
                  world, one project at a time, using technology to make a
                  meaningful impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div
        style={{
          position: "absolute",
          top: currentScene === "contact" ? "700vh" : "-10000px", // Position AT the scroll target (0.925 * 800vh)
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: currentScene === "contact" ? "auto" : "none",
          padding: "2rem",
          color: "white",
        }}
      >
        <h2
          data-animate-id="contact-title"
          className={`text-7xl font-bold text-white text-center mb-4 md:mb-6 lg:mb-8 transition-all duration-1000 ease-out ${
            visibleElements.has("contact-title")
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-10"
          }`}
          style={{ fontFamily: "Azonix, sans-serif" }}
        >
          let's talk
        </h2>
        <div className="flex flex-row gap-4 justify-center">
          <a
            href="mailto:cheryltqr2907@gmail.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white px-4 py-2 underline underline-offset-4 focus:outline-none"
            style={{
              color: "white",
              background: "transparent",
              backgroundColor: "transparent",
              border: "none",
              borderColor: "transparent",
              outline: "none",
              cursor: "pointer",
              transform: "scale(1)",
              transformOrigin: "center",
              transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "Modeka, sans-serif",
              fontSize: "1.5rem",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.2)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Email
          </a>
          <a
            href="https://github.com/cheryl-toh"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white px-4 py-2 underline underline-offset-4 focus:outline-none"
            style={{
              color: "white",
              background: "transparent",
              backgroundColor: "transparent",
              border: "none",
              borderColor: "transparent",
              outline: "none",
              cursor: "pointer",
              transform: "scale(1)",
              transformOrigin: "center",
              transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "Modeka, sans-serif",
              fontSize: "1.5rem",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.2)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            GitHub
          </a>
          <a
            href="https://www.linkedin.com/in/cheryl-toh-5854a51b3/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white px-4 py-2 underline underline-offset-4 focus:outline-none"
            style={{
              color: "white",
              background: "transparent",
              backgroundColor: "transparent",
              border: "none",
              borderColor: "transparent",
              outline: "none",
              cursor: "pointer",
              transform: "scale(1)",
              transformOrigin: "center",
              transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "Modeka, sans-serif",
              fontSize: "1.5rem",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.2)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            LinkedIn
          </a>
        </div>
      </div>
    </Scroll>
  );
};

export default HTMLSections;
